import type { AggregatedMetricPanelConfig } from '@src/types/LayoutConfig';
import { FormControlSizes, HStack, IconButton, IconName } from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  resolveMetricDisplayName,
  resolveSignalsValueFormatter,
  sameStringList,
  SIGNALS_DEFAULT_TIME_RANGE,
  SignalsLineChart,
  SignalsModuleShell,
  SignalsTimeRangeControl,
  useMetricDisplayNameMap,
  useSignalsAssetMap,
  type SignalsTimeRange,
} from '../core';
import {
  formatAggregatedMetricDefaultTitle,
  type AggregatedMetricModuleConfig,
  type AggregationMethod,
} from './AggregatedMetric.types';
import { AggregatedMetricConfigView } from './AggregatedMetricConfigView';
import { AggregatedMetricToolbar } from './AggregatedMetricToolbar';
import { useAggregatedMetricData } from './useAggregatedMetricData';
import { useAvailableMetrics } from './useAvailableMetrics';

type View = 'chart' | 'config';

interface AggregatedMetricModuleProps {
  /** Immutable per-panel-type defaults (default metric, default assets, default aggregation, frequency, label). */
  config: AggregatedMetricModuleConfig;
  /** Persisted panel state hydrated from the layout config. */
  params: AggregatedMetricPanelConfig;
  /** Persists a partial update to the layout config (delegates to `api.updateParameters`). */
  onParamsChange: (partial: Partial<AggregatedMetricPanelConfig>) => void;
  /** Current tab title — empty string when the panel is using the metric-derived default. */
  title: string;
  /** Fires when the user edits the title in the config view; delegates to `api.setTitle` in the panel. */
  onTitleChange: (next: string) => void;
}

/**
 * Top-level module rendered inside `AggregatedMetricPanel`. Owns the session-only
 * view state and delegates persisted state (selections, aggregation, time range,
 * metric, title) to the parent via `onParamsChange`.
 */
export function AggregatedMetricModule({
  config,
  params,
  onParamsChange,
  title,
  onTitleChange,
}: AggregatedMetricModuleProps) {
  // A new panel ships with a default metric, asset basket and aggregation (all editable from the
  // header toolbar), so it lands straight in the chart — the add-panel factory no longer sets
  // `settingsInitialOpen`. The flag is still honoured if present (e.g. a caller opens config
  // explicitly), and the effect below clears it so a reload returns to the chart.
  const [view, setView] = useState<View>(() => (params.settingsInitialOpen === true ? 'config' : 'chart'));

  // Effective state — `params` is the source of truth, defaults only fill in absent fields.
  const metric = params.metric ?? config.defaultMetric;
  const selectedCmAssetIds = params.selectedCmAssetIds ?? config.defaultCmAssetIds;
  const aggregationMethod = params.aggregationMethod ?? config.defaultAggregationMethod;
  const timeRange = params.timeRange ?? SIGNALS_DEFAULT_TIME_RANGE;
  // SignalsLineChart captures the formatter via a ref, so a new identity each render is fine — no memo.
  const formatValue = resolveSignalsValueFormatter(metric);

  const { displayNameByMetricId } = useMetricDisplayNameMap();
  const getMetricLabel = useCallback(
    (metricId: string) => resolveMetricDisplayName(metricId, displayNameByMetricId),
    [displayNameByMetricId]
  );
  const defaultTitle = formatAggregatedMetricDefaultTitle(getMetricLabel(metric));

  // Clear `settingsInitialOpen` on mount so a later reload opens in the chart view, not config.
  useEffect(() => {
    if (params.settingsInitialOpen) {
      onParamsChange({ settingsInitialOpen: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Asset map owned here (one subscription per panel), used for the stale-asset self-heal below
  // and the symbol↔CM-id translation behind both the toolbar and config asset controls.
  const metricsForAssetMap = useMemo(() => [metric], [metric]);
  const {
    talosSymbols: availableTalosSymbols,
    cmAssetIdByTalosSymbol,
    talosSymbolByCmAssetId,
    isLoading: isAssetsLoading,
    isError: isAssetsError,
  } = useSignalsAssetMap(metricsForAssetMap);

  useEffect(() => {
    if (isAssetsLoading || isAssetsError) {
      return;
    }
    // `params.selectedCmAssetIds == null` means a new panel using the hard-coded default basket.
    // Filter it the same way as a persisted selection so hidden/unsupported assets can't silently
    // skew the aggregate, then persist the result so subsequent renders see the clean list.
    const current = params.selectedCmAssetIds ?? config.defaultCmAssetIds;
    const filtered = current.filter(cmAssetId => talosSymbolByCmAssetId.has(cmAssetId));
    if (filtered.length !== current.length || params.selectedCmAssetIds == null) {
      onParamsChange({ selectedCmAssetIds: filtered });
    }
  }, [
    isAssetsLoading,
    isAssetsError,
    talosSymbolByCmAssetId,
    params.selectedCmAssetIds,
    config.defaultCmAssetIds,
    onParamsChange,
  ]);

  // Intersection of metrics supported by every currently selected asset. Calling this hook
  // at the module level (always, not just inside the config view) pre-warms the CoinMetrics
  // catalog query so the metric picker doesn't have to wait on a cold ~4s fetch when the
  // user first opens settings.
  const {
    metrics: availableMetrics,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
  } = useAvailableMetrics(selectedCmAssetIds);

  const { aggregatedSeries, isFetching, isError, isTruncated } = useAggregatedMetricData({
    metric,
    frequency: config.frequency,
    // Series name surfaces in the chart tooltip — using the metric-derived title keeps
    // the tooltip informative when the user switches metrics on the same panel.
    seriesLabel: defaultTitle,
    selectedCmAssetIds,
    aggregationMethod,
    timeRange,
  });

  // Both the toolbar and the config view speak Talos symbols; translate to/from CoinMetrics IDs
  // here so the module stays the single owner of the symbol↔id mapping.
  const selectedTalosSymbols = useMemo(() => {
    const symbols: string[] = [];
    for (const cmAssetId of selectedCmAssetIds) {
      const talosSymbol = talosSymbolByCmAssetId.get(cmAssetId);
      if (talosSymbol != null) {
        symbols.push(talosSymbol);
      }
    }
    return symbols;
  }, [selectedCmAssetIds, talosSymbolByCmAssetId]);

  // Every `onParamsChange` writes dockview panel params, which persists and rerenders — so each
  // handler skips no-op writes (translated IDs / unchanged value equal to what's already stored).
  const handleSelectedTalosSymbolsChange = useCallback(
    (talosSymbols: string[]) => {
      const cmAssetIds: string[] = [];
      for (const talosSymbol of talosSymbols) {
        const cmAssetId = cmAssetIdByTalosSymbol.get(talosSymbol);
        if (cmAssetId != null) {
          cmAssetIds.push(cmAssetId);
        }
      }
      if (!sameStringList(cmAssetIds, selectedCmAssetIds)) {
        onParamsChange({ selectedCmAssetIds: cmAssetIds });
      }
    },
    [cmAssetIdByTalosSymbol, selectedCmAssetIds, onParamsChange]
  );

  const handleAggregationMethodChange = useCallback(
    (method: AggregationMethod) => {
      if (method !== aggregationMethod) {
        onParamsChange({ aggregationMethod: method });
      }
    },
    [aggregationMethod, onParamsChange]
  );

  const handleMetricChange = useCallback(
    (nextMetric: string) => {
      if (nextMetric !== metric) {
        onParamsChange({ metric: nextMetric });
      }
    },
    [metric, onParamsChange]
  );

  const handleTimeRangeChange = useCallback(
    (next: SignalsTimeRange) => {
      if (next !== timeRange) {
        onParamsChange({ timeRange: next });
      }
    },
    [timeRange, onParamsChange]
  );

  const handleOpenConfig = useCallback(() => setView('config'), []);
  const handleCloseConfig = useCallback(() => setView('chart'), []);

  if (view === 'config') {
    return (
      <AggregatedMetricConfigView
        title={title}
        titlePlaceholder={defaultTitle}
        onTitleChange={onTitleChange}
        metric={metric}
        availableMetrics={availableMetrics}
        isMetricsLoading={isMetricsLoading}
        isMetricsError={isMetricsError}
        onMetricChange={handleMetricChange}
        getMetricLabel={getMetricLabel}
        selectedTalosSymbols={selectedTalosSymbols}
        availableTalosSymbols={availableTalosSymbols}
        onSelectedTalosSymbolsChange={handleSelectedTalosSymbolsChange}
        aggregationMethod={aggregationMethod}
        onAggregationMethodChange={handleAggregationMethodChange}
        onClose={handleCloseConfig}
      />
    );
  }

  const isLoading = isFetching && aggregatedSeries.at(0)?.data?.length === 0;
  // A truncated response means the series is silently incomplete — treat it as an error
  // state with an actionable message rather than charting a misleading partial line.
  const isErrorOrTruncated = isError || isTruncated;
  const errorMessage = isTruncated
    ? 'Too many assets for the selected range. Remove some assets or choose a shorter time range.'
    : `Failed to load ${config.label}.`;

  return (
    <SignalsModuleShell
      header={
        <AggregatedMetricToolbar
          metric={metric}
          availableMetrics={availableMetrics}
          isMetricsLoading={isMetricsLoading}
          isMetricsError={isMetricsError}
          onMetricChange={handleMetricChange}
          getMetricLabel={getMetricLabel}
          selectedTalosSymbols={selectedTalosSymbols}
          availableTalosSymbols={availableTalosSymbols}
          onSelectedTalosSymbolsChange={handleSelectedTalosSymbolsChange}
          aggregationMethod={aggregationMethod}
          onAggregationMethodChange={handleAggregationMethodChange}
        />
      }
      isLoading={isLoading}
      isError={isErrorOrTruncated}
      errorMessage={errorMessage}
      controls={
        <HStack gap="spacing8">
          <SignalsTimeRangeControl
            value={timeRange}
            onChange={handleTimeRangeChange}
            disabled={isFetching || isErrorOrTruncated}
          />
          <IconButton
            icon={IconName.Cog}
            onClick={handleOpenConfig}
            size={FormControlSizes.Small}
            data-testid="aggregated-metric-open-config"
          />
        </HStack>
      }
    >
      <SignalsLineChart series={aggregatedSeries} formatValue={formatValue} />
    </SignalsModuleShell>
  );
}
